import { useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ApiContext } from "../../context/ApiContext";
import useStepData from "../../hooks/useStepData";
import apiRequest from "../../requests/apiRequest";
import Button from "../../shared/Button";
import Loader from "../../shared/Loader";
import { FormInputs } from "../../types/types";
import __ from "../../utils/getContent";
import getCookie from "../../utils/getCookie";
import { getPricingUrlWithLocation } from "../../utils/getClubSlugFromName";
import EmailField from "../fields/emailField";
import SelectField from "../fields/selectField";
import TelField from "../fields/telField";
import TextField from "../fields/textField";

const StepContact = (): React.JSX.Element => {
	const {
		handleSubmit,
		trigger,
		getValues,
		watch,
		unregister,
		clearErrors,
	} = useFormContext<FormInputs>();

	const { data, setData } = useContext(ApiContext);

	const [isLoading, setIsLoading] = useState(false);
	const [numLookupAttempts, setNumLookupAttempts] = useState(0);
	const [submitError, setSubmitError] = useState("");
	const { error, loading, stepData } = useStepData(0);
	const navigate = useNavigate();

	const clubId = watch("clubId");
	const requireCheckPrice = stepData?.Clubs?.find(
		(club: any) => club.HubSpotCode === clubId,
	)?.RequireCheckPrice;

	const contactFieldNames = [
		"member.firstName",
		"member.lastName",
		"member.emailAddress",
		"member.mobilePhone",
	] as const;

	useEffect(() => {
		if (!requireCheckPrice) {
			unregister([...contactFieldNames]);
			clearErrors([...contactFieldNames]);
		}
	}, [requireCheckPrice, unregister, clearErrors]);

	const goToNext = async () => {
		const hubSpotCode = getValues("clubId");
		const selectedClub = stepData?.Clubs?.find(
			(club: any) => club.HubSpotCode === hubSpotCode,
		);

		if (!requireCheckPrice) {
			if (!selectedClub) {
				return;
			}

			setData({
				...data,
				get_club_id: selectedClub.ClubID,
				selectedClub,
			});

			navigate(getPricingUrlWithLocation(selectedClub));
			return;
		}

		// Validate field and return if errors.
		const isClubValid = await trigger("clubId");
		const isEmailValid = await trigger("email");
		if (!isClubValid || !isEmailValid) {
			return;
		}

		setIsLoading(true);

		const response = await apiRequest("createHubspotContact", [
			{ club: getValues("clubId") },
			{ firstname: getValues("member.firstName") },
			{ lastname: getValues("member.lastName") },
			{ email: getValues("member.emailAddress") },
			{ mobilephone: getValues("member.mobilePhone") },
			{
				context: {
					pageUri: window.location.href,
					hutk: getCookie("hubspotutk"), // include the hubspotutk cookie value.
				},
			},
		]);

		if (response?.success) {
			contactLookup();
		} else {
			setSubmitError(response?.message);
			setIsLoading(false);
		}
	};

	const contactLookup = async () => {
		const email = getValues("member.emailAddress");
		const response = await apiRequest("getContact", [{ email }]);
		const { data } = response || {};

if (data?.contactId) {
	const hubSpotCode = getValues("clubId");

	const selectedClub = stepData.Clubs.find(
		(club: any) => club.HubSpotCode === hubSpotCode,
	);

			// Append email to contact response.
			const contact = { ...data, email };

			setData({
				...data,
				...{ get_club_id: selectedClub.ClubID },
				...{ contact_id: contact },
				...{ selectedClub },
			});

			navigate(getPricingUrlWithLocation(selectedClub));
		} else {
			if (numLookupAttempts < 3) {
				setNumLookupAttempts(numLookupAttempts + 1);
				setTimeout(() => {
					contactLookup();
				}, 1000);
			} else {
				setSubmitError("Could not find contact.");
				setIsLoading(false);
			}
		}
	};

	if (loading) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<Loader colorCircle="#000" colorRotator="#ddd" />
			</div>
		);
	}

	if (error) {
		console.error(error);
		return <h1>Error!</h1>;
	}

	return (
		<div className="step-contact mb-10 flex max-w-[600px] flex-col self-center">
			<h1 className="mb-1 text-center text-3xl uppercase">
				{__("heading_one_step_away", "YOU'RE ONLY ONE STEP AWAY")}
			</h1>
			<p className="mb-4 text-center">
				{__(
					"contact_description",
					"We just need a few things to send a personalized membership quote to your inbox.",
				)}
			</p>
			<div className="border border-black p-4">
				<form
					onSubmit={(event) => {
						event.preventDefault();
						if (!requireCheckPrice) {
							goToNext();
							return;
						}
						handleSubmit(goToNext)(event);
					}}
				>
					{stepData && (
						<SelectField
							registeredName="clubId"
							label={__("input_club_label", "Club")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
							options={stepData?.Clubs?.map((club: any) => ({
								key: club?.HubSpotCode,
								value: club?.HubSpotCode,
								text: club?.Name,
								requireCheckPrice: club?.RequireCheckPrice,
							}))}
						/>
					)}

					{requireCheckPrice && (
						<>
							<div className="flex gap-2">
								<TextField
									registeredName="member.firstName"
									label={__("input_first_name_label", "First Name")}
									isRequired={true}
									requiredText={__(
										"input_required",
										"This field is required.",
									)}
									placeholder={__("input_first_name_label", "First Name")}
								/>

								<TextField
									registeredName="member.lastName"
									label={__("input_last_name_label", "Last Name")}
									isRequired={true}
									requiredText={__(
										"input_required",
										"This field is required.",
									)}
									placeholder={__("input_last_name_label", "Last Name")}
								/>
							</div>

							<EmailField
								registeredName="member.emailAddress"
								label={__("input_email_label", "Email")}
								requiredText={__(
									"email_required",
									"Please enter a valid email address.",
								)}
								isRequired={true}
								placeholder={__("input_email_label", "Email")}
							/>

							<TelField
								registeredName="member.mobilePhone"
								label={__("input_mobile_phone_label", "Mobile Phone")}
								isRequired={true}
								requiredText={__(
									"phone_required",
									"Please enter a valid phone number.",
								)}
								placeholder={__("input_mobile_phone_label", "Mobile Phone")}
							/>

							<div
								className="mb-4 text-sm italic opacity-70"
								dangerouslySetInnerHTML={{
									__html: __(
										"privacy_policy_text",
										"By submitting this form I acknowledge and agree to Midtown Athletic Club's <a href='#'>Privacy Policy</a> and Terms of Use. We will never share or sell your personal information. This is used for club communications only.",
									),
								}}
							/>
						</>
					)}

					<Button isLoading={isLoading} buttonType="submit">
						{__("button_text_next_step", "Next Step")}
					</Button>

					{submitError && (
						<p className="mt-2 text-sm text-red-500">
							{__("error_message_text", "Error: ")}
							{submitError}
						</p>
					)}
				</form>
			</div>
		</div>
	);
};

export default StepContact;
